import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PhoneFrame } from "../components/devices/PhoneFrame";
import { AIDemoOverlay, ComposeBar } from "../components/humn-ai/AIDemo";
import { RichSLMScreen, MicroSLMScreen, CreditsScreen } from "../components/devices/screens";
import { StoreButtons } from "../components/StoreButtons";
import { Reveal, MaskedLines } from "../components/motion/Reveal";
import { usePageMeta } from "../lib/usePageMeta";

const PageShell = ({ eyebrow, titleLines, intro, children, testId }) => (
  <main className="scene scene-purple pt-36 pb-28 px-6 min-h-screen" data-testid={testId}>
    <div className="max-w-5xl mx-auto">
      <p className="eyebrow mb-5">{eyebrow}</p>
      <MaskedLines as="h1" className="h-display h-xl max-w-3xl" lines={titleLines} delay={0.1} />
      {intro && (
        <Reveal delay={0.25}>
          <p className="mt-7 tx-2 leading-relaxed max-w-xl text-lg">{intro}</p>
        </Reveal>
      )}
      {children}
    </div>
  </main>
);

export function AboutPage() {
  usePageMeta("About — HUMN", "HUMN is a social intelligence app that turns intention into action.");
  const beliefs = [
    ["01", "Intention over attention", "Social technology should start from what you want to do, not what keeps you scrolling."],
    ["02", "People over content", "The most valuable thing a network can surface is the right person at the right moment."],
    ["03", "Action over feeds", "A discovery only matters if it becomes a conversation, a booking, a collaboration, a journey."],
    ["04", "Value moves through people", "When people teach, host, help and create, that value should be recognized and rewarded."],
  ];
  return (
    <PageShell
      eyebrow="About HUMN"
      titleLines={["We build for what", "people want to do."]}
      intro="Search engines help us find information. AI helps us understand it. HUMN helps us act with people. We call this the Social Language Model — a living layer between intention and human action."
      testId="about-page"
    >
      <div className="mt-20 border-t border-[color:var(--border-soft)]">
        {beliefs.map(([n, t, d], i) => (
          <Reveal key={n} delay={i * 0.06}>
            <div className="grid sm:grid-cols-12 gap-4 py-10 border-b border-[color:var(--border-soft)] items-baseline">
              <span className="sm:col-span-1 font-mono-ui text-xs tx-3">{n}</span>
              <h2 className="sm:col-span-4 h-display text-2xl">{t}</h2>
              <p className="sm:col-span-7 tx-2 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          <StoreButtons placement="about" />
          <Link to="/contact" data-testid="about-contact-link" className="text-sm underline underline-offset-4 tx-2 hover:opacity-100 transition-colors duration-200">Get in touch</Link>
        </div>
      </Reveal>
    </PageShell>
  );
}

export function HumnAIPage() {
  usePageMeta("HUMN AI — Say what you want to do", "HUMN AI turns intention into action: chat, create, discover, plan and continue any social journey.");
  const [demoOpen, setDemoOpen] = useState(false);
  const steps = [
    ["You express an intention", "\u201CI want to organize a startup breakfast this Saturday.\u201D"],
    ["HUMN AI understands", "Intent, location, date, audience, possible CTA, relevant communities, suggested people, next step."],
    ["It becomes action", "Create the SLM (HumnAi), invite founders, find a venue, open the workspace, track it in My Happenings."],
  ];
  return (
    <PageShell
      eyebrow="HUMN AI"
      titleLines={["Say what you", "want to do."]}
      intro="Chat, create, discover, plan and continue any social journey from one intelligent compose bar."
      testId="humn-ai-page"
    >
      <Reveal delay={0.3}>
        <div className="mt-12"><ComposeBar onOpen={() => setDemoOpen(true)} /></div>
      </Reveal>
      <div className="mt-24 grid md:grid-cols-3 gap-6">
        {steps.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.1}>
            <div className="glass-2 rounded-3xl p-8 h-full">
              <p className="font-mono-ui text-xs tx-3">STEP 0{i + 1}</p>
              <h2 className="h-display text-xl mt-3">{t}</h2>
              <p className="tx-2 text-sm mt-3 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <button
          data-testid="humn-ai-page-demo-btn"
          onClick={() => setDemoOpen(true)}
          className="btn-accent mt-16 px-8 py-4 text-sm font-semibold"
        >
          Try the interactive demo
        </button>
      </Reveal>
      <AIDemoOverlay open={demoOpen} onClose={() => setDemoOpen(false)} />
    </PageShell>
  );
}

export function SLMPage() {
  usePageMeta("SLMs (HumnAi) — Social Language Models", "An SLM (HumnAi) does not end when it is published. It evolves as people act.");
  const types = [
    { n: "Rich SLM (HumnAi)", d: "Real media uploaded by the creator. Events, restaurants, trips, workshops, products, mentorships, projects.", Screen: RichSLMScreen },
    { n: "Micro SLM (HumnAi)", d: "Short, lightweight and immediate. \u201CNeed a Flutter mentor.\u201D \u201CCoffee at 5?\u201D \u201CWeekend cycling?\u201D", Screen: MicroSLMScreen },
    { n: "Live SLM (HumnAi)", d: "Continuously updates through progress, people and actions. Funding, learning journeys, challenges, bookings, communities.", Screen: CreditsScreen },
  ];
  return (
    <PageShell
      eyebrow="Social Language Models"
      titleLines={["A post is published.", "An SLM (HumnAi) evolves."]}
      intro="An SLM (HumnAi) does not end when it is published. It evolves as people discover it, join it, talk inside it, reach milestones and create new opportunities from it. Creators upload authentic photos or videos for Rich SLMs (HumnAi) — HUMN AI structures the SLM (HumnAi) but never fabricates its real-world visual identity."
      testId="slm-page"
    >
      <div className="mt-24 space-y-28">
        {types.map((t, i) => (
          <Reveal key={t.n}>
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <p className="font-mono-ui text-xs tracking-widest" style={{ color: "var(--pink)" }}>TYPE 0{i + 1}</p>
                <h2 className="h-display h-lg mt-3">{t.n}</h2>
                <p className="tx-2 mt-5 leading-relaxed max-w-md text-lg">{t.d}</p>
              </div>
              <div className="flex justify-center">
                <PhoneFrame className="w-[250px] h-[520px]" testId={`slm-phone-${i + 1}`}>
                  <t.Screen animate={false} />
                </PhoneFrame>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </PageShell>
  );
}

export function CreditsPage() {
  usePageMeta("HUMN Credits — Your actions create value", "HUMN Credits power discovery, participation, creation and rewards across the network.");
  const earn = [["Teach a session", "+120"], ["Host an experience", "+80"], ["Help a community", "+40"], ["Complete a challenge", "+25"], ["Create useful value", "+60"]];
  const uses = ["Book", "Learn", "Support", "Unlock", "Promote", "Join", "Access", "Collaborate"];
  return (
    <PageShell
      eyebrow="HUMN Credits"
      titleLines={["Your actions", "create value."]}
      intro="HUMN Credits power discovery, participation, creation and rewards across the network. Meaningful actions — teaching, mentoring, hosting, helping — create value that moves through people."
      testId="credits-page"
    >
      <div className="mt-20 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <div className="glass-3 glass-reflect rounded-[32px] p-10">
            <div className="relative z-10">
            <p className="eyebrow mb-6">Ways people earn</p>
            <div className="space-y-4">
              {earn.map(([t, v]) => (
                <div key={t} className="flex justify-between items-center border-b pb-4" style={{ borderColor: "var(--border-soft)" }}>
                  <span className="tx-1">{t}</span>
                  <span className="font-mono-ui" style={{ color: "var(--pink)" }}>{v}</span>
                </div>
              ))}
            </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow mb-6">Then use Credits to</p>
          <div className="flex flex-wrap gap-2.5">
            {uses.map((u) => (
              <span key={u} className="chip text-sm px-4 py-2">{u}</span>
            ))}
          </div>
          <p className="mt-10 tx-2 text-sm leading-relaxed">
            Users may earn Credits through approved and verified activities. Earning opportunities depend on eligibility, verification, activity type, local availability and HUMN policy.
          </p>
          <p className="mt-4 tx-3 text-xs leading-relaxed">
            Credits are not cryptocurrency, investment products or guaranteed income.
          </p>
          <Link to="/credits-policy" data-testid="credits-page-policy-link" className="inline-block mt-6 text-sm underline underline-offset-4 tx-2 hover:opacity-100 transition-colors duration-200">
            Read the full Credits Policy
          </Link>
        </Reveal>
      </div>
    </PageShell>
  );
}

export function SafetyPage() {
  usePageMeta("Safety — HUMN", "Built around real people and real intent: authenticity, control, transparency and safety.");
  const items = [
    ["Authentic Media", "Rich SLM (HumnAi) creators upload their own photos or videos. HUMN AI does not invent the visual identity of real opportunities."],
    ["Personal Control", "Control interests, recommendations, location use and AI personalization at any time."],
    ["Transparent Credits", "Credit activity, usage and rewards are shown clearly, with a full history."],
    ["Meaningful Safety", "Reporting, blocking, community guidelines, visibility controls and content moderation are built into the experience."],
    ["Respectful Data", "Your data is used to serve your intent — not to maximize your screen time. See the Privacy Policy for details."],
  ];
  return (
    <PageShell
      eyebrow="Trust & Safety"
      titleLines={["Built around real people", "and real intent."]}
      testId="safety-page"
    >
      <div className="mt-20 border-t border-[color:var(--border-soft)]">
        {items.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.05}>
            <div className="grid sm:grid-cols-12 gap-4 py-10 border-b border-[color:var(--border-soft)] items-baseline">
              <span className="sm:col-span-1 font-mono-ui text-xs tx-3">0{i + 1}</span>
              <h2 className="sm:col-span-4 h-display text-2xl">{t}</h2>
              <p className="sm:col-span-7 tx-2 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="mt-12 flex flex-wrap gap-6 text-sm tx-2">
          {[["Privacy Policy", "/privacy"], ["Community Guidelines", "/community-guidelines"], ["Content Policy", "/content-policy"], ["Credits Policy", "/credits-policy"]].map(([l, to]) => (
            <Link key={to} to={to} data-testid={`safety-link-${to.replace("/", "")}`} className="underline underline-offset-4 hover:text-black transition-colors duration-200">{l}</Link>
          ))}
        </div>
      </Reveal>
    </PageShell>
  );
}

export function NotFoundPage() {
  usePageMeta("Page not found — HUMN", "This page doesn't exist.");
  return (
    <main className="scene scene-aurora min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="not-found-page">
      <motion.img
        src="/brand/humn-mark.png" alt="" aria-hidden="true"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="h-12 w-auto mb-6 brand-float"
      />
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-ui text-sm tracking-[0.3em]" style={{ color: "var(--pink)" }}>404</motion.p>
      <h1 className="h-display h-xl mt-6">This intention led nowhere.</h1>
      <p className="tx-2 mt-5 max-w-sm leading-relaxed">The page you're looking for doesn't exist. But there's plenty you can still do.</p>
      <div className="mt-10 flex gap-4">
        <Link to="/" data-testid="not-found-home-link" className="btn-cta px-7 py-3.5 text-sm font-semibold">Back home</Link>
        <Link to="/download" data-testid="not-found-download-link" className="btn-ghost px-7 py-3.5 text-sm">Download HUMN</Link>
      </div>
    </main>
  );
}
